import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AssistantBubble from "@/components/AssistantBubble";
import { useOfficeCounts } from "@/lib/visitOffice";
import { LayoutDashboard, Users, CalendarDays, FileText, Settings, LogOut, UserCog, Repeat, Package, Inbox, TrendingUp, Images, ClipboardCheck, Menu, X } from "lucide-react";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true, testid: "nav-dashboard" },
  { to: "/admin/leads", icon: Inbox, label: "Leads", testid: "nav-leads" },
  { to: "/admin/clientes", icon: Users, label: "Clientes", testid: "nav-clientes" },
  { to: "/admin/ciclo", icon: Repeat, label: "Ciclo de servicios", testid: "nav-ciclo" },
  { to: "/admin/calendario", icon: CalendarDays, label: "Calendario", testid: "nav-calendario" },
  { to: "/admin/revision-visitas", icon: ClipboardCheck, label: "Revisión de visitas", testid: "nav-revision-visitas" },
  { to: "/admin/cotizaciones", icon: FileText, label: "Cotizaciones", testid: "nav-cotizaciones" },
  { to: "/admin/finanzas", icon: TrendingUp, label: "Finanzas", testid: "nav-finanzas" },
  { to: "/admin/catalogo", icon: Package, label: "Catálogo", testid: "nav-catalogo" },
  { to: "/admin/galeria", icon: Images, label: "Galería pública", testid: "nav-galeria", adminOnly: true },
  { to: "/admin/usuarios", icon: UserCog, label: "Usuarios y técnicos", testid: "nav-usuarios", adminOnly: true },
  { to: "/admin/ajustes", icon: Settings, label: "Ajustes", testid: "nav-ajustes" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);
  const officeCounts = useOfficeCounts();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#fafafa]" data-testid="admin-layout">
      {/* Sidebar */}
      <button data-testid="admin-mobile-menu" className="lg:hidden fixed top-3 right-3 z-50 bg-black text-white p-3" aria-label={navOpen ? "Cerrar menú" : "Abrir menú"} onClick={() => setNavOpen(!navOpen)}>{navOpen ? <X size={20} /> : <Menu size={20} />}</button>
      {navOpen && <button data-testid="admin-mobile-menu-overlay" className="lg:hidden fixed inset-0 bg-black/50 z-30" aria-label="Cerrar navegación" onClick={() => setNavOpen(false)} />}
      <aside className={`w-64 bg-[#0A0A0A] text-white flex flex-col fixed h-full overflow-y-auto z-40 transition-transform ${navOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-6 py-6 border-b border-neutral-800 bg-white">
          <img src="/aa-logo.png" alt="All Active" className="h-10 w-auto" />
        </div>

        <nav className="flex-1 py-4">
          {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              onClick={() => setNavOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm border-l-2 transition-colors ${
                  isActive
                    ? "bg-[#FF5A00] text-white border-[#FF5A00] font-medium"
                    : "text-neutral-400 border-transparent hover:bg-neutral-900 hover:text-white"
                }`
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={2} />
              <span>{item.label}</span>
              {item.to === "/admin/revision-visitas" && <span data-testid="nav-review-new-count" className="ml-auto text-xs font-bold tabular-nums">({officeCounts.new})</span>}
            </NavLink>
          ))}
        </nav>

        <div className="px-6 py-4 border-t border-neutral-800 space-y-3">
          <div>
            <div className="font-mono-kpi text-[9px] uppercase tracking-[0.2em] text-neutral-500">Sesión</div>
            <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name || user?.email}</div>
            <div className="text-xs text-neutral-500 truncate">{user?.email}</div>
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-button"
            className="w-full flex items-center justify-between bg-neutral-900 hover:bg-[#FF5A00] text-neutral-300 hover:text-white px-3 py-2 text-xs font-mono-kpi uppercase tracking-[0.15em] transition-colors"
          >
            <span>Cerrar sesión</span>
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 lg:ml-64 min-h-screen min-w-0 pt-12 lg:pt-0">
        <Outlet />
      </main>

      {/* Floating 360° assistant — solo dentro del admin */}
      <AssistantBubble />
    </div>
  );
}
